import { Calculator, Globe, MapPin, Ruler } from "@phosphor-icons/react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import { appPaths } from "../../Routes";
import { DataSourceSummary } from "../../api/models/DataSource";
import { ragToBgClassMap } from "../../constants";
import ConnectionTypeChips from "../ConnectionTypeChips";
import Typography from "../Typography";
import CardField from "./CardField";

const DataSourceTile = ({
    id,
    name,
    uniqueReference,
    connectionTypeId,
    connectionTypeName,
    connectionTypeChipColorHex,
    scope,
    locationName,
    rawUnitsSuffixHtml,
    currentNormalizedUnitsSuffixHtml: normalizedUnitsSuffixHtml,
    currentConversionFactor: conversionFactor,
    currentEmissionsFactor,
    isConnected,
}: DataSourceSummary) => {
    const navigate = useNavigate();

    return (
        <div
            className="flex flex-col rounded-xl bg-surface-light border border-surface-light-5 overflow-clip cursor-pointer"
            onClick={() => navigate(appPaths.dataSourceDetail(`${id}`))}
        >
            <div className="flex bg-deep-ocean-20 text-white px-6 py-4 gap-4">
                <div>
                    <Typography component="h3" variant="body-large">
                        {name}
                    </Typography>
                    <Typography component="span" variant="body-small">
                        {uniqueReference}
                    </Typography>
                </div>
                <div className="ml-auto flex items-center gap-2 font-semibold shrink-0">
                    <Globe weight="light" size={22} />
                    <Typography variant="title-large" component="span">
                        Scope {scope}
                    </Typography>
                </div>
            </div>
            <div className="px-6 py-4 space-y-2 mb-auto border-b-surface-light-5 border-b-1">
                <CardField
                    label="Location"
                    value={locationName}
                    icon={MapPin}
                />
                <div className="flex gap-8">
                    <CardField
                        label="Raw Units"
                        value={rawUnitsSuffixHtml}
                        isHtml
                        icon={Ruler}
                    />
                    <CardField
                        label="Normalized Units"
                        value={normalizedUnitsSuffixHtml}
                        isHtml
                    />
                </div>
                <CardField
                    label="Conversion Factor"
                    value={conversionFactor}
                    isHtml={false}
                    icon={Calculator}
                />
                <div className="flex items-center justify-between">
                    {currentEmissionsFactor ? (
                        <CardField
                            label="Emissions Factor"
                            value={currentEmissionsFactor ?? -1}
                            isHtml={false}
                            icon={Calculator}
                        />
                    ) : (
                        <div>
                            <Typography
                                component="label"
                                variant="label-small"
                                className="block mb-1"
                            >
                                Emissions Factor
                            </Typography>
                            <div
                                className={clsx(
                                    "text-white text-center rounded-md text-xs py-1 px-3 inline",
                                    ragToBgClassMap["red"]
                                )}
                            >
                                Missing
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {connectionTypeId && (
                <div className="bg-white px-6 py-4">
                    <div className="flex gap-2 items-center">
                        <ConnectionTypeChips
                            connectionTypeName={connectionTypeName}
                            connectionTypeChipColorHex={
                                connectionTypeChipColorHex
                            }
                        />
                        {connectionTypeId && (
                            <div className="flex items-center gap-2 ml-auto">
                                <div
                                    className={clsx(
                                        "w-3 h-3 flex-none rounded-full",
                                        isConnected
                                            ? "bg-soft-green-80"
                                            : "bg-red-40"
                                    )}
                                />
                                <span className="text-sm">
                                    {isConnected
                                        ? "Connected"
                                        : "Not connected"}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataSourceTile;
